import React, { useState, useRef } from 'react';
import 'leaflet/dist/leaflet.css';
import { Map } from 'react-leaflet';
import JSZip from 'jszip';
import { extractKMLCoords, useMapScreenshot, MapCaptureOverlay, MapCameraButton } from '../../util/leafletHelpers';
import LeafletBaseLayers from './LeafletBaseLayers';

/**
 * Shared base map component.
 *
 * Props:
 *   center          {[lat, lng]}  Map center (required when mapVisible is true)
 *   zoom            {number}      Initial zoom level (default 5)
 *   uploadRef       {ref}         Optional external ref for the hidden file input
 *   onCoordsExtracted {fn}        Called with raw coords array after KML/KMZ parsed
 *   minKmlPoints    {number}      Minimum coord points required from KML (default 1)
 *   loading         {bool}        Show "Processing…" indicator from parent
 *   filterBar       {JSX}         Optional UI rendered between the header and the map
 *   onMapReady      {fn}          Called with the Leaflet map instance once mounted
 *   mapVisible      {bool}        When false, renders placeholder instead of the map (default true)
 *   placeholder     {JSX}         Content shown when mapVisible is false
 *   children                      Leaflet layer components rendered inside <Map>
 */
export default function AppMap({
    center,
    zoom = 5,
    uploadRef = null,
    onCoordsExtracted,
    minKmlPoints = 1,
    loading = false,
    filterBar = null,
    onMapReady,
    mapVisible = true,
    placeholder = null,
    downloadPrefix = 'map',
    children,
}) {
    const internalRef = useRef(null);
    const fileInputRef = uploadRef || internalRef;
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');
    const [mapInstance, setMapInstance] = useState(null);
    const [activeLayer, setActiveLayer] = useState('Street')
    const { takeScreenshot, capturingMap } = useMapScreenshot(mapInstance, `${downloadPrefix}-${activeLayer}`);

    const handleMapRef = (ref) => {
        setMapInstance(ref);
        if (onMapReady) onMapReady(ref);
        if(ref?.leafletElement) {
            ref.leafletElement.on('baselayerchange', (e) => setActiveLayer(e.name));
        }
    };

    const handleFile = async (file) => {
        setUploading(true);
        setUploadError('');
        try {
            let kmlText;
            if (file.name.endsWith('.kml')) {
                kmlText = await file.text();
            } else if (file.name.endsWith('.kmz')) {
                const zip = await JSZip.loadAsync(file);
                const kmlFile = Object.values(zip.files).find(f => f.name.endsWith('.kml'));
                if (!kmlFile) throw new Error('No .kml file found inside KMZ');
                kmlText = await kmlFile.async('text');
            } else {
                throw new Error('Only .kml or .kmz files accepted');
            }
            const coords = extractKMLCoords(kmlText, minKmlPoints);
            if (onCoordsExtracted) onCoordsExtracted(coords);
        } catch (e) {
            setUploadError(e.message || 'Failed to process file');
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    return (
        <div style={{ width: '100%', overflow: 'hidden' }}>

            {/* ── Header bar ── */}
            <div style={{ background: '#1976d2', borderBottom: '1px solid #ddd', display: 'flex', alignItems: 'center', gap: 12 }}>
                <MapCaptureOverlay visible={capturingMap} />
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".kml,.kmz"
                    onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
                    style={{ display: 'none' }}
                />
                {(uploading || loading) && <span style={{ fontSize: '0.85rem', color: '#fff' }}>Processing…</span>}
            </div>

            {/* ── Optional filter / search bar ── */}
            {filterBar}

            {/* ── Upload error ── */}
            {uploadError && (
                <div style={{ padding: '6px 12px', background: '#ffebee', color: '#c62828', fontSize: '0.85rem' }}>
                    {uploadError}
                </div>
            )}

            {/* ── Map or placeholder ── */}
            {mapVisible ? (
                <div style={{ height: 'calc(100vh - 210px)', position: 'relative' }}>
                    <MapCameraButton onClick={takeScreenshot} />
                    <Map
                        center={center}
                        zoom={zoom}
                        style={{ height: '100%', width: '100%' }}
                        ref={handleMapRef}
                        preferCanvas={true}
                    >
                        <LeafletBaseLayers />
                        {children}
                    </Map>
                </div>
            ) : placeholder}
        </div>
    );
}
